// Edit on page: the resume drawn as a formatted page, with every piece of text
// typed straight into it. Sections, jobs, bullets, highlights and skills can be
// added, removed and reordered. Changes save themselves and rebuild the Word file.
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Bold, Italic, Plus, RemoveFormatting, Replace, Underline, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { jobBullets } from '@/lib/resumeFit';
import { TEMPLATE_FAMILIES } from '@/components/TemplateFamilyMenu';
import { LIST_SECTIONS, rowsOf, sectionTitles, skillsOf, type ListSection } from './resumeSections';

const barColor = (templateId?: string) => TEMPLATE_FAMILIES.flatMap((f) => f.colors).find((c) => c.id === templateId)?.swatch ?? '#17375E';

type TextStyle = { text: string; bold?: boolean; italic?: boolean; underline?: boolean };
const StylesContext = createContext<TextStyle[]>([]);

/** Shows bold, italic and underline on the page the way the Word file will have them. */
function styled(value: string, styles: TextStyle[]): ReactNode {
  const hit = styles.filter((x) => x.text && value.includes(x.text)).sort((a, b) => b.text.length - a.text.length)[0];
  if (!hit) return value;
  const cls = [hit.bold === true ? 'font-bold' : hit.bold === false ? 'font-normal' : '', hit.italic ? 'italic' : '', hit.underline ? 'underline' : ''].join(' ');
  const rest = styles.filter((x) => x !== hit);
  return value.split(hit.text).flatMap((part, i) => (i === 0 ? [styled(part, rest)] : [<span key={i} className={cls}>{hit.text}</span>, styled(part, rest)]));
}

function Text({ value, onChange, className, placeholder, label }: { value: string; onChange: (v: string) => void; className?: string; placeholder?: string; label: string }) {
  const styles = useContext(StylesContext);
  return (
    <span
      key={value + JSON.stringify(styles)}
      data-page-text
      role="textbox"
      aria-label={label}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      data-placeholder={placeholder}
      className={`rounded-sm outline-none hover:bg-amber-50 focus:bg-amber-50 empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] ${className ?? ''}`}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); } }}
      onBlur={(e) => { const t = e.currentTarget.innerText.replace(/\s+/g, ' ').trim(); if (t !== value) onChange(t); }}
    >{styled(value, styles)}</span>
  );
}

function Tools({ onUp, onDown, onDelete, name }: { onUp?: () => void; onDown?: () => void; onDelete?: () => void; name: string }) {
  const cls = 'rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-30';
  return (
    <span className="ml-1 inline-flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
      {onUp !== undefined && <button type="button" className={cls} onClick={onUp} aria-label={`Move ${name} up`}><ArrowUp className="h-3.5 w-3.5" /></button>}
      {onDown !== undefined && <button type="button" className={cls} onClick={onDown} aria-label={`Move ${name} down`}><ArrowDown className="h-3.5 w-3.5" /></button>}
      {onDelete && <button type="button" className={cls} onClick={onDelete} aria-label={`Delete ${name}`}><X className="h-3.5 w-3.5" /></button>}
    </span>
  );
}

const AddLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button type="button" onClick={onClick} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"><Plus className="h-3 w-3" /> {label}</button>
);

const move = <T,>(list: T[], i: number, d: number): T[] => {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};
const without = <T,>(list: T[], i: number): T[] => list.filter((_, k) => k !== i);
const fieldKey = (item: any, keys: string[]) => keys.find((k) => typeof item?.[k] === 'string' && item[k] !== '') ?? keys[0];

export function PageEditor({ json, templateId, onChange }: { json: any; templateId?: string; onChange: (next: any) => void }) {
  const { toast } = useToast();
  const color = barColor(templateId);
  const [find, setFind] = useState('');
  const [repl, setRepl] = useState('');
  const layout = json?.ResumeLayout ?? {};
  const titles = useMemo(() => sectionTitles(json), [json]);

  const put = (patch: any) => onChange({ ...json, ...patch });
  const putLayout = (patch: any) => put({ ResumeLayout: { ...layout, ...patch } });
  const headingOf = (t: string) => layout.headingRenames?.[t] || t;

  const moveSection = (t: string, d: number) => putLayout({ sectionOrder: move(titles, titles.indexOf(t), d) });
  const textStyles: TextStyle[] = layout.textStyles ?? [];
  const selected = () => {
    const sel = window.getSelection();
    const text = sel?.rangeCount ? sel.getRangeAt(0).toString().replace(/\s+/g, ' ').trim() : '';
    const el = sel?.anchorNode?.parentElement;
    if (!text || !el?.closest('[data-page-text]')) {
      toast({ title: 'Select some words first', description: 'Highlight the words on the page, then choose bold, italic or underline.' });
      return null;
    }
    return { text, el };
  };
  const toggle = (field: 'bold' | 'italic' | 'underline') => {
    const pick = selected();
    if (!pick) return;
    const entry = textStyles.find((x) => x.text === pick.text);
    const css = getComputedStyle(pick.el);
    const now = field === 'bold' ? Number(css.fontWeight) >= 600 : field === 'italic' ? css.fontStyle === 'italic' : entry?.underline === true;
    const next = { ...(entry ?? { text: pick.text }), [field]: !now };
    putLayout({ textStyles: [...textStyles.filter((x) => x !== entry), next] });
  };
  const clearStyle = () => {
    const pick = selected();
    if (pick) putLayout({ textStyles: textStyles.filter((x) => x.text !== pick.text) });
  };
  const bar = (title: string, onDelete?: () => void) => (
    <div className="group my-2 flex items-center justify-center px-2 py-0.5 text-sm font-semibold text-white" style={{ backgroundColor: color, marginLeft: -36, marginRight: -36 }}>
      <Text label={`${title} heading`} value={headingOf(title)} className="hover:bg-white/20 focus:bg-white/20" onChange={(v) => putLayout({ headingRenames: { ...(layout.headingRenames ?? {}), [title]: v || title } })} />
      <span className="[&_button]:text-white/70 [&_button:hover]:bg-white/20 [&_button:hover]:text-white">
        <Tools name={`${title} section`} onUp={() => moveSection(title, -1)} onDown={() => moveSection(title, 1)} onDelete={onDelete} />
      </span>
    </div>
  );

  const countMatches = (v: any): number => typeof v === 'string' ? v.split(find).length - 1
    : Array.isArray(v) ? v.reduce((s, x) => s + countMatches(x), 0)
    : v && typeof v === 'object' ? Object.entries(v).reduce((s, [k, x]) => s + (k === 'ResumeLayout' ? 0 : countMatches(x)), 0) : 0;
  const replaceIn = (v: any): any => typeof v === 'string' ? v.split(find).join(repl)
    : Array.isArray(v) ? v.map(replaceIn)
    : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k === 'ResumeLayout' ? x : replaceIn(x)])) : v;
  const matches = find ? countMatches(json) : 0;

  const skills = skillsOf(json?.SkillRows, 'Skill');
  const tech = skillsOf(json?.TechnicalSkillRows, 'TechSkill');
  const skillList = (list: string[], field: string, prefix: string, label: string) => (
    <div className="text-center leading-6">
      {list.map((s, i) => (
        <span key={`${i}-${s}`} className="group inline-flex items-center">
          {i > 0 && <span className="mx-1 text-slate-500">•</span>}
          <Text label={`${label} ${i + 1}`} value={s} onChange={(v) => put({ [field]: rowsOf(v ? list.map((x, k) => (k === i ? v : x)) : without(list, i), prefix) })} />
          <Tools name={s} onUp={() => put({ [field]: rowsOf(move(list, i, -1), prefix) })} onDown={() => put({ [field]: rowsOf(move(list, i, 1), prefix) })} onDelete={() => put({ [field]: rowsOf(without(list, i), prefix) })} />
        </span>
      ))}
      <div><AddLink label={`Add ${label.toLowerCase()}`} onClick={() => put({ [field]: rowsOf([...list, 'New skill'], prefix) })} /></div>
    </div>
  );

  const jobs: any[] = json?.ProfessionalExperience ?? [];
  const putJob = (ji: number, patch: any) => put({ ProfessionalExperience: jobs.map((j, k) => (k === ji ? { ...j, ...patch } : j)) });
  // A job split into named groups keeps its groups; a flat job uses Descriptions.
  const putBullets = (ji: number, bullets: any[]) => {
    const j = jobs[ji];
    if (Array.isArray(j.Groups) && j.Groups.length) {
      let at = 0;
      const groups = j.Groups.map((g: any, gi: number) => {
        const isLast = gi === j.Groups.length - 1;
        const n = (g.Descriptions ?? []).length;
        const part = isLast ? bullets.slice(at) : bullets.slice(at, at + n);
        at += n;
        return { ...g, Descriptions: part };
      });
      putJob(ji, { Groups: groups });
    } else putJob(ji, { Descriptions: bullets });
  };

  const highlights: any[] = json?.CareerHighlights ?? [];
  const education: any[] = json?.Education ?? [];

  const listSection = (s: ListSection) => {
    const items: any[] = json?.[s.key] ?? [];
    return (
      <div key={s.key}>
        {bar(s.title, () => { const next = { ...json }; delete next[s.key]; onChange(next); })}
        <div className="text-center">
          {items.map((it, i) => (
            <div key={i} className="group flex items-center justify-center gap-1">
              {s.fields.map((f, fi) => {
                const k = fieldKey(it, f.keys);
                if (fi > 0 && !it?.[k]) return <Text key={f.label} label={f.label} value="" placeholder={` ${f.label}`} onChange={(v) => put({ [s.key]: items.map((x, n) => (n === i ? { ...x, [k]: v } : x)) })} />;
                return (
                  <span key={f.label} className={fi === 0 ? 'font-semibold' : ''}>
                    {fi > 0 && <span className="mx-1">–</span>}
                    <Text label={f.label} value={String(it?.[k] ?? '')} onChange={(v) => put({ [s.key]: items.map((x, n) => (n === i ? { ...x, [k]: v } : x)) })} />
                  </span>
                );
              })}
              <Tools name="item" onUp={() => put({ [s.key]: move(items, i, -1) })} onDown={() => put({ [s.key]: move(items, i, 1) })} onDelete={() => put({ [s.key]: without(items, i) })} />
            </div>
          ))}
          <AddLink label="Add item" onClick={() => put({ [s.key]: [...items, { [s.fields[0].keys[0]]: 'New item' }] })} />
        </div>
      </div>
    );
  };

  const sectionBody = (t: string) => {
    if (t === 'Skills') return <div key={t}>{bar(t)}{skillList(skills, 'SkillRows', 'Skill', 'Skill')}</div>;
    if (t === 'Technical Skills') return <div key={t}>{bar(t, () => put({ TechnicalSkillRows: [] }))}{skillList(tech, 'TechnicalSkillRows', 'TechSkill', 'Technical skill')}</div>;
    if (t === 'Career Highlights') return (
      <div key={t}>
        {bar(t, () => put({ CareerHighlights: [] }))}
        {highlights.map((h, i) => (
          <div key={i} className="group mb-1.5 flex items-start pl-6">
            <span className="mr-1.5">✓</span>
            <div className="min-w-0 flex-1">
              <Text label={`Highlight ${i + 1} title`} className="font-semibold" value={String(h?.chtitle ?? '')} onChange={(v) => put({ CareerHighlights: highlights.map((x, k) => (k === i ? { ...x, chtitle: v } : x)) })} />
              <span className="font-semibold">: </span>
              <Text label={`Highlight ${i + 1}`} value={String(h?.chdescription ?? '')} onChange={(v) => put({ CareerHighlights: highlights.map((x, k) => (k === i ? { ...x, chdescription: v } : x)) })} />
            </div>
            <Tools name="highlight" onUp={() => put({ CareerHighlights: move(highlights, i, -1) })} onDown={() => put({ CareerHighlights: move(highlights, i, 1) })} onDelete={() => put({ CareerHighlights: without(highlights, i) })} />
          </div>
        ))}
        <AddLink label="Add highlight" onClick={() => put({ CareerHighlights: [...highlights, { chtitle: 'New Highlight', chdescription: 'Describe the result here.' }] })} />
      </div>
    );
    if (t === 'Professional Experience') return (
      <div key={t}>
        {bar(t)}
        {jobs.map((j, ji) => {
          const bullets = jobBullets(j);
          return (
            <div key={ji} className="mb-3">
              <div className="group flex items-center">
                <Text label={`Job ${ji + 1} title`} className="font-semibold" value={String(j?.job_title ?? '')} onChange={(v) => putJob(ji, { job_title: v })} />
                <span style={{ color }} />
                <Tools name="job" onUp={() => put({ ProfessionalExperience: move(jobs, ji, -1) })} onDown={() => put({ ProfessionalExperience: move(jobs, ji, 1) })} onDelete={() => put({ ProfessionalExperience: without(jobs, ji) })} />
              </div>
              <div className="font-semibold">
                <Text label="Company" value={String(j?.company ?? '')} placeholder="Company" onChange={(v) => putJob(ji, { company: v })} /> |{' '}
                <Text label="Location" value={String(j?.location ?? '')} placeholder="Location" onChange={(v) => putJob(ji, { location: v })} /> |{' '}
                <Text label="Start date" value={String(j?.start_date ?? '')} placeholder="Start" onChange={(v) => putJob(ji, { start_date: v })} /> to{' '}
                <Text label="End date" value={String(j?.end_date ?? '')} placeholder="End" onChange={(v) => putJob(ji, { end_date: v })} />
              </div>
              <ul className="mt-1 list-disc pl-8">
                {bullets.map((b: any, bi: number) => (
                  <li key={bi} className="group">
                    <span className="flex items-start">
                      <span className="min-w-0 flex-1"><Text label={`Job ${ji + 1} bullet ${bi + 1}`} value={String(b?.Description ?? '')} onChange={(v) => putBullets(ji, bullets.map((x: any, k: number) => (k === bi ? { ...x, Description: v } : x)))} /></span>
                      <Tools name="bullet" onUp={() => putBullets(ji, move(bullets, bi, -1))} onDown={() => putBullets(ji, move(bullets, bi, 1))} onDelete={() => putBullets(ji, without(bullets, bi))} />
                    </span>
                  </li>
                ))}
              </ul>
              <AddLink label="Add bullet" onClick={() => putBullets(ji, [...bullets, { Description: 'New bullet' }])} />
            </div>
          );
        })}
        <AddLink label="Add job" onClick={() => put({ ProfessionalExperience: [{ job_title: 'New Job Title', company: 'Company', location: 'City, ST', start_date: 'Start', end_date: 'Present', Descriptions: [{ Description: 'New bullet' }] }, ...jobs] })} />
      </div>
    );
    if (t === 'Education') return (
      <div key={t}>
        {bar(t, () => put({ Education: [] }))}
        <div className="text-center">
          {education.map((e, i) => (
            <div key={i} className="group mb-1">
              <div className="flex items-center justify-center">
                <Text label="Degree" className="font-semibold" value={String(e?.College_Degree_Name ?? '')} onChange={(v) => put({ Education: education.map((x, k) => (k === i ? { ...x, College_Degree_Name: v } : x)) })} />
                <Tools name="education" onUp={() => put({ Education: move(education, i, -1) })} onDown={() => put({ Education: move(education, i, 1) })} onDelete={() => put({ Education: without(education, i) })} />
              </div>
              {e?.Major ? <div><Text label="Major" value={String(e.Major)} onChange={(v) => put({ Education: education.map((x, k) => (k === i ? { ...x, Major: v } : x)) })} /></div> : null}
              <div><Text label="School" value={String(e?.College_Name ?? '')} placeholder="School" onChange={(v) => put({ Education: education.map((x, k) => (k === i ? { ...x, College_Name: v } : x)) })} /></div>
            </div>
          ))}
          <AddLink label="Add education" onClick={() => put({ Education: [...education, { College_Degree_Name: 'New Degree', College_Name: 'School' }] })} />
        </div>
      </div>
    );
    const s = LIST_SECTIONS.find((x) => x.title === t);
    return s ? listSection(s) : null;
  };

  const addable = LIST_SECTIONS.filter((s) => !titles.includes(s.title));

  return (
    <StylesContext.Provider value={textStyles}>
    <div className="flex h-full flex-col bg-slate-100">
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-3 py-1.5 text-xs">
        {([['bold', Bold, 'Bold'], ['italic', Italic, 'Italic'], ['underline', Underline, 'Underline']] as const).map(([field, Icon, name]) => (
          <Button key={field} type="button" size="sm" variant="outline" className="h-7 w-7 p-0" aria-label={name} title={name} onMouseDown={(e) => { e.preventDefault(); toggle(field); }}>
            <Icon className="h-3.5 w-3.5" />
          </Button>
        ))}
        <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" aria-label="Clear bold, italic and underline" title="Clear bold, italic and underline" onMouseDown={(e) => { e.preventDefault(); clearStyle(); }}>
          <RemoveFormatting className="h-3.5 w-3.5" />
        </Button>
        <input aria-label="Find" placeholder="Find" value={find} onChange={(e) => setFind(e.target.value)} className="h-7 w-32 rounded border px-2" />
        <input aria-label="Replace with" placeholder="Replace with" value={repl} onChange={(e) => setRepl(e.target.value)} className="h-7 w-32 rounded border px-2" />
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={!matches} onClick={() => { onChange(replaceIn(json)); toast({ title: 'Replaced', description: `${matches} match${matches === 1 ? '' : 'es'} changed.` }); }}>
          <Replace className="h-3.5 w-3.5" /> Replace all{find ? ` (${matches})` : ''}
        </Button>
        <span className="text-muted-foreground">Click any text to type. Misspelled words get a red underline. Hover for move and delete. Bold, italic and underline apply everywhere the same words appear.</span>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div className="mx-auto w-full max-w-[816px] overflow-hidden bg-white text-[13px] leading-snug text-black shadow" style={{ fontFamily: 'Aptos, Calibri, "Segoe UI", sans-serif' }}>
          <div className="px-6 py-5 text-center text-white" style={{ backgroundColor: color }}>
            <div className="text-3xl font-bold"><Text label="Name" className="hover:bg-white/20 focus:bg-white/20" value={String(json?.Name ?? '')} onChange={(v) => put({ Name: v })} /></div>
            <div className="mt-1 text-xs">
              <Text label="Phone" className="hover:bg-white/20 focus:bg-white/20" value={String(json?.Phone ?? '')} onChange={(v) => put({ Phone: v })} /> •{' '}
              <Text label="Email" className="hover:bg-white/20 focus:bg-white/20" value={String(json?.Email ?? '')} onChange={(v) => put({ Email: v })} /> •{' '}
              <Text label="LinkedIn" className="hover:bg-white/20 focus:bg-white/20" value={String(json?.LinkedIn ?? '')} onChange={(v) => put({ LinkedIn: v })} />
            </div>
          </div>
          <div className="px-9 py-3">
            <div className="text-center font-semibold"><Text label="Headline title" value={String(json?.HeadlineTitle ?? '')} onChange={(v) => put({ HeadlineTitle: v })} /></div>
            <div className="mb-2 text-center"><Text label="Headline results" value={String(json?.HeadlineMetrics ?? '')} onChange={(v) => put({ HeadlineMetrics: v })} /></div>
            <div className="text-justify"><Text label="Career summary" value={String(json?.Summary ?? '')} onChange={(v) => { const n = { ...json, Summary: v }; delete n.SummaryIndentTwips; onChange(n); }} /></div>
            {titles.map(sectionBody)}
            {addable.length > 0 && (
              <label className="mt-3 flex items-center gap-2 text-xs">
                <Plus className="h-3 w-3 text-sky-700" />
                <select aria-label="Add a section" className="h-7 rounded border bg-background px-1 text-xs" value="" onChange={(e) => { const s = LIST_SECTIONS.find((x) => x.key === e.target.value); if (s) put({ [s.key]: [{ [s.fields[0].keys[0]]: 'New item' }] }); }}>
                  <option value="">Add a section...</option>
                  {addable.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
    </StylesContext.Provider>
  );
}
